import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import { onAnimationEnd } from '@theme/utilities';

/**
 * A custom element that manages the main menu drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDetailsElement} details - The details element.
 *
 * @extends {Component<Refs>}
 */
class HeaderDrawer extends Component {
  requiredRefs = ['details'];

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('keyup', this.#onKeyUp);
    this.#setupAnimatedElementListeners();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keyup', this.#onKeyUp);
    window.removeEventListener('resize', this.#updateDrawerTop);
    this.#releasePanelFocus();
  }

  /**
   * Close the main menu drawer when the Escape key is pressed
   * @param {KeyboardEvent} event
   */
  #onKeyUp = (event) => {
    if (event.key !== 'Escape') return;

    const openDetails = event.target instanceof Element ? event.target.closest('details[open]') : null;
    this.#close(openDetails instanceof HTMLDetailsElement ? openDetails : this.#getDetailsElement(event));
  };

  /**
   * @returns {boolean} Whether the main menu drawer is open
   */
  get isOpen() {
    return this.refs.details.hasAttribute('open');
  }

  /**
   * Get the closest details element to the event target
   * @param {Event | undefined} event
   * @returns {HTMLDetailsElement}
   */
  #getDetailsElement(event) {
    if (!(event?.target instanceof Element)) return this.refs.details;

    return event.target.closest('details') ?? this.refs.details;
  }

  /**
   * Toggle the main menu drawer
   */
  toggle(event) {
    if (this.isOpen && this.#isSlidingMenu && event instanceof Event) {
      // Let the menu animate closed instead of the browser closing it instantly
      event.preventDefault();
    }
    return this.isOpen ? this.close() : this.open();
  }

  /**
   * @returns {boolean} Whether the drawer uses the sliding menu layout
   */
  get #isSlidingMenu() {
    return this.querySelector('.menu-drawer--sliding') !== null;
  }

  /**
   * On mobile, the sliding menu opens below the header bar. Measure where the header ends.
   * The menu starts 1px higher (over the header's hidden bottom line) so that, whatever the
   * phone's pixel density, there is never a hairline gap between the header and the menu.
   */
  #updateDrawerTop = () => {
    if (!this.#isSlidingMenu) return;

    const header = this.closest('header-component') ?? this.closest('header');
    const bottom = header ? header.getBoundingClientRect().bottom : 0;
    this.style.setProperty('--mobile-drawer-top', `${Math.max(0, bottom - 1)}px`);
  };

  /**
   * Open the closest drawer or the main menu drawer
   * @param {Event} [event]
   */
  open(event) {
    const details = this.#getDetailsElement(event);
    const summary = details.querySelector('summary');

    if (!summary) return;

    summary.setAttribute('aria-expanded', 'true');

    if (details === this.refs.details) {
      this.#updateDrawerTop();
      window.addEventListener('resize', this.#updateDrawerTop);
    }

    const panel = getSlidingPanel(details);

    requestAnimationFrame(() => {
      if (panel) {
        // Make sure the panel's closed (off-screen) position is rendered first so it slides in
        void panel.offsetWidth;
      }
      details.classList.add('menu-open');
      setTimeout(() => {
        if (panel) {
          this.#trapPanelFocus(panel);
          panel.querySelector('.mobile-nav__back')?.focus({ preventScroll: true });
        } else {
          trapFocus(details);
        }
      }, 0);
    });
  }

  /**
   * Go back or close the main menu drawer
   * @param {Event} [event]
   */
  back(event) {
    this.#close(this.#getDetailsElement(event));
  }

  /**
   * Close the main menu drawer
   */
  close() {
    this.#close(this.refs.details);
  }

  /**
   * Close the closest menu or submenu that is open
   *
   * @param {HTMLDetailsElement} details
   */
  #close(details) {
    const summary = details.querySelector('summary');

    if (!summary) return;

    summary.setAttribute('aria-expanded', 'false');
    details.classList.remove('menu-open');

    const isSlidingPanel = details !== this.refs.details && getSlidingPanel(details) !== null;

    if (isSlidingPanel) {
      // Go back to the level this panel was opened from, without closing the drawer
      const parentPanel = details.parentElement?.closest('.mobile-nav__panel');

      if (parentPanel instanceof HTMLElement) {
        this.#trapPanelFocus(parentPanel);
      } else {
        this.#releasePanelFocus();
        trapFocus(this.refs.details);
      }
      summary.focus({ preventScroll: true });
    }

    onAnimationEnd(details, () => {
      // The level was opened again while it was closing
      if (details.classList.contains('menu-open')) return;

      reset(details);

      if (details === this.refs.details) {
        window.removeEventListener('resize', this.#updateDrawerTop);
        removeTrapFocus();
        this.#releasePanelFocus();
        const openDetails = this.querySelectorAll('details[open]');
        openDetails.forEach(reset);
      } else if (!isSlidingPanel) {
        setTimeout(() => {
          trapFocus(this.refs.details);
        }, 0);
      }
    });
  }

  /** @type {{ keydown: (event: KeyboardEvent) => void, focusin: (event: FocusEvent) => void } | null} */
  #panelFocusHandlers = null;

  /**
   * Keep keyboard focus inside the active submenu panel.
   * Only elements that are actually visible take part, so items of hidden levels are skipped.
   * @param {HTMLElement} panel
   */
  #trapPanelFocus(panel) {
    removeTrapFocus();
    this.#releasePanelFocus();

    // The menu toggle in the header (shown as the X while the menu is open) stays reachable
    const menuToggle = this.refs.details.querySelector(':scope > summary');
    /** @param {EventTarget | null} target */
    const isInside = (target) => target instanceof Node && (panel.contains(target) || target === menuToggle);
    const getFocusable = () => {
      const items = getVisibleFocusableElements(panel);
      return menuToggle instanceof HTMLElement && isVisible(menuToggle) ? [menuToggle, ...items] : items;
    };

    /** @param {KeyboardEvent} event */
    const keydown = (event) => {
      if (event.key !== 'Tab') return;

      const focusable = getFocusable();
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (!first || !last) return;

      const active = document.activeElement;
      const outside = !isInside(active);

      if (!event.shiftKey && (active === last || outside)) {
        event.preventDefault();
        first.focus();
      } else if (event.shiftKey && (active === first || active === panel || outside)) {
        event.preventDefault();
        last.focus();
      }
    };

    /** @param {FocusEvent} event */
    const focusin = (event) => {
      if (event.target instanceof Node && !isInside(event.target)) {
        event.stopPropagation();
        getFocusable()[0]?.focus();
      }
    };

    document.addEventListener('keydown', keydown, true);
    document.addEventListener('focusin', focusin, true);
    this.#panelFocusHandlers = { keydown, focusin };
  }

  #releasePanelFocus() {
    if (!this.#panelFocusHandlers) return;

    document.removeEventListener('keydown', this.#panelFocusHandlers.keydown, true);
    document.removeEventListener('focusin', this.#panelFocusHandlers.focusin, true);
    this.#panelFocusHandlers = null;
  }

  /**
   * Attach animationend event listeners to all animated elements to remove will-change after animation
   * to remove the stacking context and allow submenus to be positioned correctly
   */
  #setupAnimatedElementListeners() {
    /**
     * @param {AnimationEvent} event
     */
    function removeWillChangeOnAnimationEnd(event) {
      const target = event.target;
      if (target && target instanceof HTMLElement) {
        target.style.setProperty('will-change', 'unset');
        target.removeEventListener('animationend', removeWillChangeOnAnimationEnd);
      }
    }
    const allAnimated = this.querySelectorAll('.menu-drawer__animated-element');
    allAnimated.forEach((element) => {
      element.addEventListener('animationend', removeWillChangeOnAnimationEnd);
    });
  }
}

if (!customElements.get('header-drawer')) {
  customElements.define('header-drawer', HeaderDrawer);
}

/**
 * Get the sliding submenu panel of a menu details element, if it has one
 *
 * @param {HTMLDetailsElement} details
 * @returns {HTMLElement | null}
 */
function getSlidingPanel(details) {
  const panel = details.querySelector(':scope > .mobile-nav__panel');
  return panel instanceof HTMLElement ? panel : null;
}

/**
 * Get the focusable elements of a container that are currently visible
 *
 * @param {HTMLElement} container
 * @returns {HTMLElement[]}
 */
function getVisibleFocusableElements(container) {
  const selector =
    "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), input:not([type=hidden]):enabled, select:enabled, textarea:enabled";

  return Array.from(container.querySelectorAll(selector)).filter(
    (element) => element instanceof HTMLElement && isVisible(element)
  );
}

/**
 * Whether an element is rendered and not hidden
 *
 * @param {HTMLElement} element
 * @returns {boolean}
 */
function isVisible(element) {
  if (typeof element.checkVisibility === 'function') {
    return element.checkVisibility({ visibilityProperty: true, checkVisibilityCSS: true });
  }
  return element.getClientRects().length > 0 && getComputedStyle(element).visibility !== 'hidden';
}

/**
 * Reset an open details element to its original state
 *
 * @param {HTMLDetailsElement} element
 */
function reset(element) {
  element.classList.remove('menu-open');
  element.removeAttribute('open');
  element.querySelector('summary')?.setAttribute('aria-expanded', 'false');
}
